import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Deep-link a branch owner straight into the portal profile editor.
export const ownerEditPath = (branchId, tab = "profile") =>
  `/business?branch=${encodeURIComponent(branchId)}&tab=${encodeURIComponent(tab)}`;

/**
 * Pencil button surfaced on any business the current user owns. Ownership is
 * determined server-side via `branch.is_owner`; this is UI surfacing only.
 * Stops propagation so it never triggers the parent card's navigation.
 */
export const OwnerEditButton = ({ branch, tab = "profile", className, size = "icon", variant = "secondary", withLabel = false }) => {
  const nav = useNavigate();
  if (!branch?.is_owner) return null;
  const go = (e) => {
    e.preventDefault();
    e.stopPropagation();
    nav(ownerEditPath(branch.id, tab));
  };
  return (
    <Button
      type="button"
      size={withLabel ? "sm" : size}
      variant={variant}
      onClick={go}
      aria-label="Edit your business"
      title="Edit your business"
      data-testid={`owner-edit-${branch.id}`}
      className={cn("shadow-sm", className)}
    >
      <Pencil className="h-4 w-4" />
      {withLabel && <span>Edit</span>}
    </Button>
  );
};

/** Subtle "Your business" indicator shown on owned cards / profiles. */
export const YourBusinessPill = ({ branch, className }) => {
  if (!branch?.is_owner) return null;
  return (
    <span
      data-testid={`your-business-${branch.id}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-brand-green/10 px-2 py-0.5 text-[11px] font-semibold text-brand-green ring-1 ring-inset ring-brand-green/20",
        className
      )}
    >
      <Pencil className="h-3 w-3" />
      Your business
    </span>
  );
};
